/* eslint-disable react/jsx-no-duplicate-props */
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { motion } from "framer-motion";
import gsap from "gsap";
import { useRef } from "react";
import { ReactComponent as UrlSvg } from "../url.svg";

const projects = [
  {
    num: "01",
    name: "UV Todo App",
    description:
      "Feature-rich task management application with drag-and-drop, priority levels, and real-time state sync. Built with React and Redux for a seamless productivity workflow.",
    tech: ["ReactJS", "Redux", "CSS3", "LocalStorage"],
    url: "https://uv-todo.netlify.app/",
    accent: "#00C9A7",
  },
  {
    num: "02",
    name: "Portfolio v2.0",
    description:
      "This very portfolio — engineered with GSAP ScrollTrigger, Framer Motion scramble text, SVG circular gauges, venetian-blind page loader, and layered mouse parallax.",
    tech: ["ReactJS", "GSAP", "Framer Motion", "Material-UI"],
    url: "#",
    accent: "#159895",
  },
  {
    num: "03",
    name: "Financial Dashboard",
    description:
      "Real-time financial data platform featuring interactive Chart.js graphs, WebSocket live feeds, and lazy-loading strategies that cut initial load time by 35%.",
    tech: ["ReactJS", "Chart.js", "WebSockets", "TypeScript"],
    url: "#",
    accent: "#7C3AED",
  },
  {
    num: "04",
    name: "GraphQL B2B Platform",
    description:
      "Scalable B2B SaaS platform with Hasura GraphQL subscriptions, role-based access control, and a custom component library serving thousands of active users.",
    tech: ["ReactJS", "GraphQL", "Hasura", "Material-UI"],
    url: "#",
    accent: "#FFB347",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Project card with GSAP mouse tilt + Framer Motion scroll entrance
// ─────────────────────────────────────────────────────────────────────────────
const ProjectCard = ({ project, index }) => {
  const tiltRef = useRef(null);

  const onMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    gsap.to(el, {
      rotateX: -((e.clientY - r.top) / r.height - 0.5) * 8,
      rotateY: ((e.clientX - r.left) / r.width - 0.5) * 12,
      transformPerspective: 800,
      duration: 0.4,
      ease: "power2.out",
    });
  };

  const onLeave = (e) => {
    e.currentTarget.style.boxShadow = "none";
    gsap.to(tiltRef.current, {
      rotateX: 0, rotateY: 0,
      duration: 0.65, ease: "elastic.out(1, 0.5)",
    });
  };

  return (
    // Clip-path wipe-in from top: each card reveals downward on scroll
    <motion.div
      initial={{ opacity: 0, y: 50, clipPath: "inset(0 0 100% 0)" }}
      whileInView={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 2) * 0.14, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: "800px" }}
    >
      <div
        ref={tiltRef}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{
          borderRadius: 16,
          border: `1px solid ${project.accent}33`,
          background: `${project.accent}07`,
          padding: "36px 32px",
          position: "relative",
          overflow: "hidden",
          height: "100%",
          willChange: "transform",
          transition: "box-shadow 0.35s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 8px 40px ${project.accent}22`; }}
      >
        {/* Watermark number */}
        <div style={{
          position: "absolute", bottom: -16, right: 20,
          fontSize: "clamp(80px, 14vw, 140px)",
          fontWeight: 900, fontFamily: "Poppins",
          opacity: 0.04, lineHeight: 1, userSelect: "none", pointerEvents: "none",
          letterSpacing: "-0.06em",
        }}>
          {project.num}
        </div>

        {/* Card number label */}
        <Typography
          fontFamily="Poppins" fontSize={11} fontWeight={600}
          sx={{ letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.4, mb: 1 }}
        >
          {project.num}
        </Typography>

        {/* Accent separator */}
        <div style={{
          height: 1.5, width: "36%",
          background: `linear-gradient(90deg, ${project.accent}, transparent)`,
          marginBottom: 24,
        }} />

        {/* Title */}
        <Typography
          fontFamily="Alkatra" fontWeight={700}
          sx={{ fontSize: { xs: "26px", md: "32px" }, lineHeight: 1.1, mb: 1.5 }}
        >
          {project.name}
        </Typography>

        {/* Description */}
        <Typography
          fontFamily="Poppins" fontSize={14}
          sx={{ lineHeight: 1.8, opacity: 0.65, mb: 3 }}
        >
          {project.description}
        </Typography>

        {/* Tech tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
          {project.tech.map((t) => (
            <span
              key={t}
              style={{
                padding: "4px 13px", borderRadius: 100, fontSize: 11,
                fontFamily: "Poppins", fontWeight: 500,
                border: `1px solid ${project.accent}`,
                color: project.accent,
              }}
            >
              {t}
            </span>
          ))}
        </div>

        {/* Link */}
        {project.url !== "#" && (
          <Tooltip title="View Project" arrow>
            <IconButton
              href={project.url}
              target="_blank"
              sx={{
                border: `1px solid ${project.accent}44`,
                padding: "10px",
                "&:hover": { borderColor: project.accent, boxShadow: `0 0 20px 2px ${project.accent}44` },
              }}
            >
              <UrlSvg />
            </IconButton>
          </Tooltip>
        )}

        {/* Bottom accent bar */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          height: 2.5,
          background: `linear-gradient(90deg, ${project.accent}, transparent)`,
        }} />
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Projects = () => {
  return (
    <Grid
      minHeight="100vh"
      justifyContent="center"
      alignItems="flex-start"
      display="flex"
      py={2}
      container
      component="div"
      id="projects"
    >
      {/* Section heading */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: "100%" }}
      >
        <Grid
          item
          style={{ borderRadius: "0px 40px 0px 40px" }}
          mt={10} mb={6}
          alignItems="center" justifyContent="center"
          display="flex" xs={12} border="solid 1px"
        >
          <Typography fontFamily="Alkatra" fontSize={30} fontWeight={700}>
            Projects
          </Typography>
        </Grid>
      </motion.div>

      {/* 2-column project grid */}
      <Grid item xs={12}>
        <Grid container spacing={3}>
          {projects.map((project, i) => (
            <Grid item xs={12} md={6} key={project.num}>
              <ProjectCard project={project} index={i} />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Projects;
